import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { IoArrowBack, IoChevronForward, IoReceiptOutline, IoDocumentTextOutline } from "react-icons/io5";
import { useAuth } from "shared/providers/auth";
import { getOrcamentosAbertos } from "requests/ClinicalBudget";
import { IOrcamentoAberto } from "shared/types/ClinicalBudget";
import * as Styled from "./styles";

interface ILocationState {
  pac_name?: string;
  pac_id?: number;
}

interface IPacienteInfo {
  id: number;
  nome: string;
  dt_nascimento: string | null;
  telefone: string | null;
}

const ClinicalBudgetList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { idPacient } = useParams<{ idPacient: string }>();

  const locationState = (location.state || {}) as ILocationState;

  const [loading, setLoading] = React.useState<boolean>(true);
  const [orcamentos, setOrcamentos] = React.useState<IOrcamentoAberto[]>([]);
  const [dentistaFazOrc, setDentistaFazOrc] = React.useState<number>(0);
  const [paciente, setPaciente] = React.useState<IPacienteInfo | null>(null);

  const pacientId = parseInt(idPacient || "0", 10);
  const pacientName = paciente?.nome || locationState.pac_name || "";

  React.useEffect(() => {
    if (pacientId > 0) {
      fetchOrcamentos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pacientId]);

  const fetchOrcamentos = async () => {
    setLoading(true);
    try {
      const userId = user?.data?.id || "";
      const { data, status } = await getOrcamentosAbertos(userId, pacientId);

      if ([200, 201].includes(status) && data.success) {
        setOrcamentos(data.data);
        setDentistaFazOrc(data.dentista_faz_orc ?? 0);
        if (data.paciente) {
          setPaciente(data.paciente);
        }
      } else {
        setOrcamentos([]);
      }
    } catch (error) {
      console.error("Erro ao buscar orçamentos:", error);
      setOrcamentos([]);
    } finally {
      setLoading(false);
    }
  };

  const formatMoeda = (valor: number): string => {
    return valor.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleSelectOrcamento = (orc: IOrcamentoAberto) => {
    const personData = {
      pac_id: pacientId,
      pac_name: pacientName,
      dentista_faz_orc: dentistaFazOrc,
    };
    localStorage.setItem("personAttendance", JSON.stringify(personData));
    navigate(`/clinical-budget-edit/${orc.id}`);
  };

  const patientInitial = pacientName ? pacientName.charAt(0).toUpperCase() : "#";

  return (
    <Styled.Container>
      <Styled.Header>
        <button onClick={() => navigate(-1)}>
          <IoArrowBack size={24} />
        </button>
        <div>
          <h4>Orçamentos Clínicos</h4>
        </div>
      </Styled.Header>

      <Styled.PatientCard>
        <Styled.PatientAvatar>{patientInitial}</Styled.PatientAvatar>
        <Styled.PatientInfo>
          <Styled.PatientNameText>
            {pacientName || (loading ? "Carregando..." : `ID ${pacientId}`)}
          </Styled.PatientNameText>
          <Styled.PatientMeta>
            {paciente?.dt_nascimento && (
              <span>Nasc: {paciente.dt_nascimento}</span>
            )}
            {paciente?.telefone && (
              <span>Tel: {paciente.telefone}</span>
            )}
            <span>Cód: {pacientId}</span>
          </Styled.PatientMeta>
        </Styled.PatientInfo>
      </Styled.PatientCard>

      <Styled.Content>
        {loading ? (
          <Styled.LoadingBox>
            <CircularProgress />
          </Styled.LoadingBox>
        ) : orcamentos.length > 0 ? (
          <>
            <Styled.SectionTitle>
              {orcamentos.length} orçamento(s) em aberto
            </Styled.SectionTitle>
            {orcamentos.map((orc) => (
              <Styled.Card key={orc.id} onClick={() => handleSelectOrcamento(orc)}>
                <Styled.CardHeader>
                  <Styled.CardTitle>
                    <IoReceiptOutline size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
                    Orçamento #{orc.id}
                  </Styled.CardTitle>
                  <Styled.CardBadge>{orc.total_itens} item(s)</Styled.CardBadge>
                </Styled.CardHeader>
                <Styled.CardBody>
                  <Styled.CardRow>
                    <Styled.CardLabel>Criado em</Styled.CardLabel>
                    <Styled.CardValue>{orc.dt_abertura}</Styled.CardValue>
                  </Styled.CardRow>
                  <Styled.CardRow>
                    <Styled.CardLabel>Total</Styled.CardLabel>
                    <Styled.CardValue highlight>
                      {dentistaFazOrc === 1 ? `R$ ${formatMoeda(orc.vl_total)}` : "***"}
                    </Styled.CardValue>
                  </Styled.CardRow>
                  {dentistaFazOrc === 1 && orc.vl_desconto > 0 && (
                    <Styled.CardRow>
                      <Styled.CardLabel>Desconto</Styled.CardLabel>
                      <Styled.CardValue>R$ {formatMoeda(orc.vl_desconto)}</Styled.CardValue>
                    </Styled.CardRow>
                  )}
                  {dentistaFazOrc === 1 && (
                    <Styled.CardRow>
                      <Styled.CardLabel>Parcelas</Styled.CardLabel>
                      <Styled.CardValue>{orc.parcelas}x</Styled.CardValue>
                    </Styled.CardRow>
                  )}
                  {orc.dt_vencimento && (
                    <Styled.CardRow>
                      <Styled.CardLabel>1º Vencimento</Styled.CardLabel>
                      <Styled.CardValue>{orc.dt_vencimento}</Styled.CardValue>
                    </Styled.CardRow>
                  )}
                </Styled.CardBody>
                <Styled.CardFooter>
                  Editar orçamento <IoChevronForward size={14} />
                </Styled.CardFooter>
              </Styled.Card>
            ))}
          </>
        ) : (
          <Styled.EmptyState>
            <IoDocumentTextOutline size={56} />
            <p>Nenhum orçamento em aberto</p>
            <span>Não existem orçamentos clínicos abertos criados por você para este paciente.</span>
          </Styled.EmptyState>
        )}
      </Styled.Content>
    </Styled.Container>
  );
};

export { ClinicalBudgetList };
